import { Injectable } from '@angular/core';
import { AngularFirestoreCollection } from '@angular/fire/firestore';

import { Observable, ReplaySubject, Subject } from 'rxjs';

import { Translation } from '../../models';
import { AuthenticationService } from '../authentication.service';

import { TranslationContentService } from '../translation-content.service';

import { FirestoreService } from './firestore.service';

/**
 * Provides translations from firestore database.
 *
 * @export
 */
@Injectable()
export class FirestoreTranslationService implements TranslationContentService {
    private _translationsSubject: Subject<Translation[]>;
    private _translationsFallbackSubject: Subject<Translation[]>;

    private get collection(): AngularFirestoreCollection<any> {
        return this.firestoreService.collection<any>('translations');
    }

    private get tenantCollection(): AngularFirestoreCollection<any> {
        return this.firestoreService.tenantDependentCollection<any>(this.authenticationService.user.tenantId, 'translations');
    }

    /**
     * Creates an instance of FirestoreChannelsService.
     */
    constructor(private authenticationService: AuthenticationService, private firestoreService: FirestoreService) {
        this._translationsSubject = new ReplaySubject<Translation[]>(1);
        this._translationsFallbackSubject = new ReplaySubject<Translation[]>(1);

        this.init();
    }

    /**
     * Provides an observable will all available fallback translations.
     */
    getTranslation(): Observable<Translation[]> {
        return this._translationsFallbackSubject.asObservable();
    }

    /**
     * Provides an observable will all available translations for the current tenant.
     */
    getTenantTranslation(): Observable<Translation[]> {
        return this._translationsSubject.asObservable();
    }

    private async init(): Promise<void> {
        this.collection.snapshotChanges().subscribe((resFallback) => {
            const translationsFallback = resFallback.map((docFallback) => {
                const translationFallback: Translation = {
                    id: docFallback.payload.doc.id,
                    content: docFallback.payload.doc.data(),
                };

                return translationFallback;
            });

            this._translationsFallbackSubject.next(translationsFallback);
        });

        this.tenantCollection.snapshotChanges().subscribe((res) => {
            const translations = res.map((doc) => {
                const translation: Translation = {
                    id: doc.payload.doc.id,
                    content: doc.payload.doc.data(),
                };

                return translation;
            });

            this._translationsSubject.next(translations);
        });
    }
}
